import {
  useCallback,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type ReactElement,
  type SyntheticEvent,
} from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { Autocomplete, Box, TextField, useTheme, type SxProps, type Theme } from '@mui/material'
import type { TextFieldProps } from '@mui/material/TextField'
import { replaceTrailingFormulaPlainText } from '@ui/forms/MarkdownEditor/extensions/MathCalculator'

const defaultSuggestionListboxSx = {
  '& .MuiAutocomplete-option': {
    fontSize: 12,
    lineHeight: 1.25,
    alignItems: 'flex-start',
    whiteSpace: 'normal',
    py: 0.5,
  },
} as const

export type SuggestingTextFieldProps = {
  name: string
  suggestions: string[]
  placeholder?: string
  disabled?: boolean
  minRows?: number
  maxRows?: number
  variant?: TextFieldProps['variant']
  containerSx?: SxProps<Theme>
  textFieldSx?: SxProps<Theme>
  InputProps?: TextFieldProps['InputProps']
  onClick?: TextFieldProps['onClick']
  onMouseDown?: TextFieldProps['onMouseDown']
  onFocus?: TextFieldProps['onFocus']
  /** Called when the field loses focus, with the current value. */
  onFieldBlur?: (value: string) => void
  /** Return false to keep the suggestion popup closed (e.g. unsaved-changes guard). */
  canOpen?: () => boolean
  /** Show the suggestion list when the field is focused (even before typing). */
  openOnFocus?: boolean
  listboxSx?: SxProps<Theme>
  formulaResultDecimalPlaces?: number
  onChangeText?: (newValue: string | null) => void
}

const SuggestingTextField = ({
  name,
  suggestions,
  placeholder = '',
  disabled = false,
  minRows = 3,
  maxRows,
  variant = 'outlined',
  containerSx,
  textFieldSx,
  InputProps,
  onClick,
  onMouseDown,
  onFocus,
  onFieldBlur,
  canOpen,
  openOnFocus = false,
  listboxSx,
  formulaResultDecimalPlaces = 2,
  onChangeText,
}: SuggestingTextFieldProps): ReactElement => {
  const theme = useTheme()
  const { control } = useFormContext()
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({ name, control })

  const stringValue = value ?? ''
  const [open, setOpen] = useState(false)
  const popupControlled = canOpen != null

  const handleOpen = useCallback(() => {
    if (canOpen && !canOpen()) {
      setOpen(false)
      return
    }
    setOpen(true)
  }, [canOpen])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const applyValue = useCallback(
    (raw: string, caret?: number) => {
      const { text: replaced, caretIndex } = replaceTrailingFormulaPlainText(
        raw,
        formulaResultDecimalPlaces,
        caret ?? raw.length,
      )
      onChange(replaced)
      return { replaced, caretIndex }
    },
    [formulaResultDecimalPlaces, onChange],
  )

  const handleChange = useCallback(
    (_event: SyntheticEvent, newValue: string | null) => {
      if (typeof newValue === 'string') {
        applyValue(newValue)
      }
    },
    [applyValue],
  )

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = event.target.value
      const caret = event.target.selectionStart ?? raw.length
      const { replaced, caretIndex } = applyValue(raw, caret)
      if (replaced !== raw && caretIndex !== null) {
        queueMicrotask(() => {
          event.target.setSelectionRange(caretIndex, caretIndex)
        })
      }
    },
    [applyValue],
  )

  return (
    <Box sx={{ width: '100%', ...containerSx }}>
      <Autocomplete
        freeSolo
        disableClearable
        forcePopupIcon={false}
        openOnFocus={openOnFocus}
        open={popupControlled ? open : undefined}
        onOpen={popupControlled ? handleOpen : undefined}
        onClose={popupControlled ? handleClose : undefined}
        options={suggestions}
        inputValue={stringValue}
        onChange={(event, newValue) => {
          onChangeText?.(newValue)
          handleChange(event, newValue)
        }}
        onBlur={onBlur}
        disabled={disabled}
        filterOptions={(options, { inputValue }) => {
          const query = inputValue.trim()
          if (!query) return openOnFocus ? options : []
          const lowerQuery = query.toLowerCase()
          return options.filter(option => option.toLowerCase().includes(lowerQuery))
        }}
        slotProps={{
          listbox: {
            sx: {
              ...defaultSuggestionListboxSx,
              ...listboxSx,
            },
          },
        }}
        renderInput={params => {
          const {
            value: _autocompleteInputValue,
            onFocus: autocompleteInputOnFocus,
            onBlur: autocompleteInputOnBlur,
            ...restInputProps
          } = params.inputProps

          const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
            if (canOpen && !canOpen()) {
              setOpen(false)
              event.target.blur()
              onFocus?.(event)
              return
            }

            autocompleteInputOnFocus?.(event)
            onFocus?.(event)
          }

          const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
            if (popupControlled) {
              setOpen(false)
            }
            autocompleteInputOnBlur?.(event)
            onFieldBlur?.(stringValue)
          }

          return (
            <TextField
              {...params}
              inputProps={{
                ...restInputProps,
                onFocus: handleInputFocus,
                onBlur: handleInputBlur,
              }}
              value={stringValue}
              onChange={event => {
                handleTextFieldChange(event)
                onChangeText?.(event.target.value)
              }}
              placeholder={placeholder}
              disabled={disabled}
              multiline
              minRows={minRows}
              maxRows={maxRows}
              fullWidth
              variant={variant}
              size="small"
              onClick={onClick}
              onMouseDown={onMouseDown}
              InputProps={{
                ...params.InputProps,
                ...InputProps,
              }}
              inputRef={node => {
                if (typeof params.InputProps.ref === 'function') {
                  params.InputProps.ref(node)
                }
                ref(node)
              }}
              sx={{
                ...(variant === 'outlined'
                  ? {
                      '& .MuiOutlinedInput-root': {
                        fontSize: 13,
                        fontFamily: 'Roboto',
                        lineHeight: 1.45,
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.05)'
                            : theme.palette.background.paper,
                        paddingRight: '12px !important',
                      },
                      '& .MuiOutlinedInput-inputMultiline': {
                        maxHeight: 204,
                        overflow: 'auto',
                        padding: '8px 12px',
                      },
                    }
                  : {}),
                ...textFieldSx,
              }}
            />
          )
        }}
      />
    </Box>
  )
}

export default SuggestingTextField
